<template>
  <!--
    HuaweiBatteryCoach — one-time guide for Huawei Android users.

    Why this exists:
      Huawei devices (no GMS / no Google Play Services post-EMUI 10) ship with
      aggressive EMUI/HarmonyOS battery management that kills background apps
      and silently drops web-push subscriptions. The reliable-rail Telegram
      fallback (contract Section 2 / orchestration Task 4) IS the workaround for
      these devices — push delivery is best-effort, Telegram is the safety net.
      This sheet teaches the user the two settings that restore push
      reliability so they get the FAST rail (push) and the Telegram rail only
      as backup, instead of always missing push and getting Telegram-only.

    Presentational: it never decides to open. The nudge scheduler
    (useEngagementNudges.js) gates it — not Telegram, a Huawei / Honor Android
    UA (isHuaweiAndroid(): "HUAWEI", "HONOR", "EMUI", "HarmonyOS" or
    "HMSCore", case-insensitive; a false positive shows the coach once), the
    `poolit:huawei-coach-dismissed-v2` key unset, not over the collector's day
    — and App.vue passes `opened` while it is the scheduler's current nudge.

    UX:
      - F7 sheet (modal mode) so the user must take action to dismiss.
      - AR-first copy (notification.huaweiCoach.*); RTL is automatic via
        document.documentElement.dir.
      - Two CTAs: "Got it" (primary) + "Close" (text link). Both flip the
        localStorage snooze so this never re-appears for this device.
      - Mounted at App.vue root so it can appear over any tab/page.
  -->
  <f7-sheet
    v-if="isVisible"
    backdrop
    :opened="open"
    :close-by-backdrop-click="false"
    :close-by-outside-click="false"
    :swipe-to-close="false"
    class="huawei-coach-sheet sheet-scroll sheet-scroll-inner"
    @sheet:closed="onClosed"
  >
    <div class="huawei-coach-content">
      <div class="huawei-coach-icon">
        <f7-icon
          f7="bolt_circle_fill"
          size="48px"
          color="orange"
        ></f7-icon>
      </div>

      <h2 class="huawei-coach-title">
        {{ $t('notification.huaweiCoach.title') }}
      </h2>

      <p class="huawei-coach-intro">
        {{ $t('notification.huaweiCoach.intro') }}
      </p>

      <ol class="huawei-coach-steps">
        <li class="huawei-coach-step">
          {{ $t('notification.huaweiCoach.step1') }}
        </li>
        <li class="huawei-coach-step">
          {{ $t('notification.huaweiCoach.step2') }}
        </li>
        <li class="huawei-coach-step">
          {{ $t('notification.huaweiCoach.step3') }}
        </li>
        <!--
          Gap G13: also whitelist the Telegram app. PowerGenie kills its
          persistent socket too, and the AppGallery/FCM Telegram build ships
          Push Service OFF by default — without these two steps the D5
          reliable-rail fallback can silently fail on no-GMS Huawei.
        -->
        <li class="huawei-coach-step">
          {{ $t('notification.huaweiCoach.step4') }}
        </li>
        <li class="huawei-coach-step">
          {{ $t('notification.huaweiCoach.step5') }}
        </li>
      </ol>

      <p class="huawei-coach-tip">
        {{ $t('notification.huaweiCoach.tip') }}
      </p>

      <div class="huawei-coach-actions">
        <f7-button
          large
          fill
          color="purple"
          class="huawei-coach-action"
          @click="dismiss"
        >
          {{ $t('notification.huaweiCoach.action') }}
        </f7-button>
        <f7-button
          class="huawei-coach-link"
          @click="dismiss"
        >
          {{ $t('notification.huaweiCoach.dismiss') }}
        </f7-button>
      </div>
    </div>
  </f7-sheet>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps({
  // Set by App.vue while the scheduler's current nudge is this coach.
  opened: { type: Boolean, default: false },
});

const emit = defineEmits(['closed']);

// localStorage key — written only by this component (on dismiss); the nudge
// scheduler (useEngagementNudges.js) reads it to decide whether to open it. Survives uninstalls
// only as long as the OS preserves the PWA's localStorage; that's by design
// (a reinstalled PWA re-prompts because the user's device may have changed
// brand). The key is namespaced under `poolit:` to coexist with other keys.
//
// Version bumped to `-v2` 2026-06-05 (review patch P10 — Sprint Change Proposal
// 2026-06-05 Track B): step4 + step5 were added to teach Telegram-app
// PowerGenie whitelisting + Push Service activation (gap G13). Pre-change
// dismissers stored `poolit:huawei-coach-dismissed = '1'` and would otherwise
// never see the critical new Telegram guidance. The `-v2` suffix re-shows the
// coach exactly once for those users (and forever stays dismissed after they
// tap "Got it" on the new version). The old key is intentionally NOT deleted —
// harmless ~5-byte localStorage entry; sweeping it requires a logout hook we
// don't need for a one-time-per-version coach.
const DISMISSED_KEY = 'poolit:huawei-coach-dismissed-v2';

const isVisible = ref(false);
const open = ref(false);

function markDismissed() {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // Storage blocked — accept the loss; the scheduler reads blocked storage
    // as dismissed, so it does not come back.
  }
}

function dismiss() {
  markDismissed();
  // Close the sheet visually; the @sheet:closed handler tears down the v-if
  // gate so re-renders don't keep the sheet mounted.
  open.value = false;
}

function onClosed() {
  isVisible.value = false;
  emit('closed');
}

function show() {
  // Defer the sheet open by one frame so the v-if mount lands before the
  // :opened watch fires the actual open animation (otherwise the backdrop
  // briefly stacks over the loading layout).
  isVisible.value = true;
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      // The scheduler may have taken it back within the frame.
      if (props.opened) open.value = true;
    });
  } else {
    open.value = true;
  }
}

// Unmounted while open (the root re-rendering): report the close, or the
// scheduler would keep waiting for it.
onBeforeUnmount(() => {
  if (isVisible.value) emit('closed');
});

watch(
  () => props.opened,
  (wanted) => {
    if (wanted) show();
    else open.value = false;
  },
  { immediate: true },
);
</script>

<style scoped>
/* Sheet content — vertical stack with comfortable touch targets. */
.huawei-coach-sheet {
  --f7-sheet-bg-color: #ffffff;
  --f7-sheet-border-color: transparent;
  height: auto;
  max-height: 90%;
}

.huawei-coach-content {
  padding:
    24px
    max(20px, env(safe-area-inset-right))
    max(20px, env(safe-area-inset-bottom))
    max(20px, env(safe-area-inset-left));
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: start;
}

.huawei-coach-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 4px;
}

.huawei-coach-title {
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
}

.huawei-coach-intro {
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  color: #444444;
}

.huawei-coach-steps {
  margin: 0;
  padding-inline-start: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.huawei-coach-step {
  font-size: 14.5px;
  line-height: 1.55;
  color: #1a1a1a;
}

.huawei-coach-tip {
  margin: 4px 0 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #777777;
  text-align: center;
}

.huawei-coach-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.huawei-coach-action {
  width: 100%;
  height: 48px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
}

.huawei-coach-link {
  color: #777777;
  font-size: 14px;
}
</style>
